import { getPrimaryColor, JEJURO, Styles } from '@orcar/common-d2c';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ORIGIN_URL } from '@/constants/common';
import { useSeo } from '@/hooks/useSeo';
import { getEventById } from './eventData';

const BLUE = getPrimaryColor(JEJURO);

/**
 * 이벤트 상세. 포스터 + 상세 설명(SEO 텍스트) + 유의사항.
 * 제주공항 렌트카/제주도 렌트카 키워드가 본문에 자연스럽게 포함된다.
 */
const EventDetailPage = () => {
  const { id = '' } = useParams();
  const event = getEventById(id);

  useSeo({
    title: event ? event.seoTitle : '이벤트 | 제주로렌트카',
    description: event
      ? event.seoDescription
      : '제주로렌트카의 이벤트와 혜택을 확인하세요.',
    image: event && ORIGIN_URL ? `${ORIGIN_URL}${event.thumbnail}` : undefined,
    url: event && ORIGIN_URL ? `${ORIGIN_URL}/event/${event.id}` : undefined,
  });

  if (!event) {
    return (
      <Center>
        <Inner>
          <NotFound>
            <p>존재하지 않는 이벤트예요.</p>
            <BackLink to='/event'>← 이벤트 목록으로</BackLink>
          </NotFound>
        </Inner>
      </Center>
    );
  }

  return (
    <Center>
      <Inner>
        <BackLink to='/event'>← 이벤트</BackLink>

        <Head>
          <Badge>{event.badge}</Badge>
          <Title>{event.title}</Title>
          <Summary>{event.summary}</Summary>
          <Period>진행 기간 · {event.period}</Period>
        </Head>

        <PosterWrap>
          <img src={event.poster} alt={`${event.title} 이벤트 포스터`} />
        </PosterWrap>

        <Intro>{event.intro}</Intro>

        {event.sections.map((section) => (
          <Section key={section.heading}>
            <SectionTitle>{section.heading}</SectionTitle>
            <List>
              {section.body.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </List>
          </Section>
        ))}

        <Notes>
          <NotesTitle>유의사항</NotesTitle>
          <NotesList>
            {event.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </NotesList>
        </Notes>

        <CtaRow>
          <Cta to='/'>제주로렌트카 예약하기</Cta>
        </CtaRow>
      </Inner>
    </Center>
  );
};

export default EventDetailPage;

const Center = styled.div`
  display: flex;
  justify-content: center;
`;

const Inner = styled.div`
  width: 100%;
  max-width: 720px;
  padding: 24px 20px 72px;
`;

const BackLink = styled(Link)`
  display: inline-block;
  ${Styles.FONT.B2};
  color: ${Styles.COLOR.GREY_03};
  text-decoration: none;
  margin-bottom: 18px;

  &:hover {
    color: ${Styles.COLOR.GREY_01};
  }
`;

const Head = styled.header`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 22px;
`;

const Badge = styled.span`
  ${Styles.FONT.B3};
  font-weight: 700;
  color: ${BLUE};
  background: ${BLUE}12;
  border-radius: 999px;
  padding: 5px 12px;
`;

const Title = styled.h1`
  ${Styles.FONT.H2};
  color: ${Styles.COLOR.GREY_01};
  margin: 4px 0 0;
`;

const Summary = styled.p`
  ${Styles.FONT.B1};
  color: ${Styles.COLOR.GREY_02};
  margin: 0;
`;

const Period = styled.span`
  ${Styles.FONT.B3};
  color: ${Styles.COLOR.GREY_04};
`;

const PosterWrap = styled.div`
  width: 100%;
  border-radius: 18px;
  overflow: hidden;
  background: ${Styles.COLOR.GREY_BG};
  margin-bottom: 28px;

  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const Intro = styled.p`
  ${Styles.FONT.B1};
  color: ${Styles.COLOR.GREY_01};
  line-height: 1.7;
  margin: 0 0 28px;
`;

const Section = styled.section`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  ${Styles.FONT.H4};
  color: ${Styles.COLOR.GREY_01};
  margin: 0 0 10px;
`;

const List = styled.ul`
  margin: 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  li {
    ${Styles.FONT.B1};
    color: ${Styles.COLOR.GREY_02};
    line-height: 1.6;
  }
`;

const Notes = styled.section`
  margin-top: 8px;
  padding: 18px 20px;
  border-radius: 12px;
  background: ${Styles.COLOR.GREY_BG};
`;

const NotesTitle = styled.h3`
  ${Styles.FONT.B1};
  font-weight: 700;
  color: ${Styles.COLOR.GREY_02};
  margin: 0 0 10px;
`;

const NotesList = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  li {
    ${Styles.FONT.B2};
    color: ${Styles.COLOR.GREY_03};
    line-height: 1.55;
  }
`;

const CtaRow = styled.div`
  margin-top: 32px;
  display: flex;
  justify-content: center;
`;

const Cta = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 220px;
  height: 52px;
  padding: 0 28px;
  border-radius: 12px;
  background: ${BLUE};
  color: ${Styles.COLOR.WHITE};
  ${Styles.FONT.B1};
  font-weight: 700;
  text-decoration: none;

  &:hover {
    filter: brightness(0.96);
  }
`;

const NotFound = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 80px 20px;
  text-align: center;

  p {
    ${Styles.FONT.H4};
    color: ${Styles.COLOR.GREY_02};
    margin: 0;
  }
`;
